import React, { useState, useEffect, useRef } from 'react';
import styles from './PrizeWheel.module.scss';

let sharedAudioContext: AudioContext | null = null;
const getAudioContext = (): AudioContext | null => {
  if (!sharedAudioContext) {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioContextClass) {
      sharedAudioContext = new AudioContextClass();
    }
  }
  if (sharedAudioContext?.state === 'suspended') {
    sharedAudioContext.resume().catch((err: unknown) => {
      console.warn('AudioContext resume failed:', err);
    });
  }
  return sharedAudioContext;
};

let winSound: HTMLAudioElement | null = null;
const playWinSound = () => {
  try {
    if (!winSound) {
      winSound = new Audio('https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3');
      winSound.volume = 0.4;
    }
    winSound.currentTime = 0;
    winSound.play().catch((err: unknown) => {
      console.warn('Audio playback prevented:', err);
    });
  } catch (err: unknown) {
    console.warn('Audio initialization error:', err);
  }
};

export interface Prize {
  id: string;
  label: string;
  subLabel?: string;
  type: 'cash' | 'spins' | 'bonus';
}

export const mapBackendPrizeToFrontend = (backendPrize: any): Prize => {
  let label = backendPrize.label;
  let subLabel = undefined;
  let type: Prize['type'] = 'cash';

  if (backendPrize.type === 'free_spin') {
    type = 'spins';
    label = String(backendPrize.freeSpinCount || 0);
    subLabel = 'Free Spins';
  } else if (backendPrize.type === 'deposit_match') {
    type = 'bonus';
  }

  return {
    id: String(backendPrize.id),
    label,
    subLabel,
    type,
  };
};

interface PrizeWheelProps {
  prizes: Prize[];
  isSpinning: boolean;
  onSpinComplete?: (prize: Prize) => void;
  targetPrizeIndex?: number;
  isHalfWheel?: boolean;
  size?: 'normal' | 'large';
}

const PrizeWheel: React.FC<PrizeWheelProps> = ({
  prizes,
  isSpinning,
  onSpinComplete,
  targetPrizeIndex,
  isHalfWheel = false,
  size = 'normal',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState(0);
  const [isVisible, setIsVisible] = useState(
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true,
  );
  const [isInViewport, setIsInViewport] = useState(true);
  const rotationRef = useRef(0);
  const onSpinCompleteRef = useRef(onSpinComplete);
  const prizesRef = useRef(prizes);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(entry.isIntersecting);
      },
      { threshold: 0.05 },
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    onSpinCompleteRef.current = onSpinComplete;
    prizesRef.current = prizes;
  }, [onSpinComplete, prizes]);

  useEffect(() => {
    if (isSpinning && targetPrizeIndex !== undefined) {
      const currentPrizes = prizesRef.current;
      const sliceAngle = 360 / currentPrizes.length;
      const extraSpins = 360 * 5;

      const midpoint = (targetPrizeIndex + 0.5) * sliceAngle;
      const targetRotation = 270 - midpoint + extraSpins;

      const startRotation = rotationRef.current;
      const currentSpins = Math.floor(startRotation / 360) * 360;
      const finalRotation = currentSpins + targetRotation;
      const totalRotationChange = finalRotation - startRotation;

      const playRouletteTick = () => {
        try {
          const ctx = getAudioContext();
          if (!ctx) return;
          const time = ctx.currentTime;

          const osc = ctx.createOscillator();
          const gain = ctx.createGain();

          osc.type = 'triangle';
          osc.frequency.setValueAtTime(1200, time);
          osc.frequency.exponentialRampToValueAtTime(150, time + 0.1);

          gain.gain.setValueAtTime(0, time);
          gain.gain.linearRampToValueAtTime(0.3, time + 0.005);
          gain.gain.exponentialRampToValueAtTime(0.001, time + 0.15);

          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(time);
          osc.stop(time + 0.2);
        } catch {
          // silent fallback
        }
      };

      const startTimer = setTimeout(() => {
        setRotation(finalRotation);
        rotationRef.current = finalRotation;

        const startTime = Date.now();
        const duration = 4000;
        let lastTickIndex = Math.floor(startRotation / sliceAngle);

        const tickLoop = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          const currentAngle = startRotation + totalRotationChange * easeProgress;

          const currentTickIndex = Math.floor(currentAngle / sliceAngle);
          if (currentTickIndex > lastTickIndex) {
            lastTickIndex = currentTickIndex;
            playRouletteTick();
          }

          if (progress < 1) {
            requestAnimationFrame(tickLoop);
          } else {
            playWinSound();
          }
        };
        requestAnimationFrame(tickLoop);
      }, 10);

      const timeout = setTimeout(() => {
        if (onSpinCompleteRef.current) {
          onSpinCompleteRef.current(currentPrizes[targetPrizeIndex]);
        }
      }, 4000);

      return () => {
        clearTimeout(startTimer);
        clearTimeout(timeout);
      };
    }
  }, [isSpinning, targetPrizeIndex]);

  const sliceAngle = 360 / prizes.length;

  const createChamberPath = (index: number) => {
    const padAngle = 1.5;
    const startAngle = ((index * sliceAngle + padAngle) * Math.PI) / 180;
    const endAngle = (((index + 1) * sliceAngle - padAngle) * Math.PI) / 180;
    const innerRadius = 35;
    const outerRadius = 138;

    const x1 = 150 + innerRadius * Math.cos(startAngle);
    const y1 = 150 + innerRadius * Math.sin(startAngle);
    const x2 = 150 + outerRadius * Math.cos(startAngle);
    const y2 = 150 + outerRadius * Math.sin(startAngle);
    const x3 = 150 + outerRadius * Math.cos(endAngle);
    const y3 = 150 + outerRadius * Math.sin(endAngle);
    const x4 = 150 + innerRadius * Math.cos(endAngle);
    const y4 = 150 + innerRadius * Math.sin(endAngle);

    return `M${x1},${y1} L${x2},${y2} A${outerRadius},${outerRadius} 0 0,1 ${x3},${y3} L${x4},${y4} A${innerRadius},${innerRadius} 0 0,0 ${x1},${y1} Z`;
  };

  const isPaused = !isVisible || !isInViewport;

  return (
    <div
      ref={containerRef}
      className={`${styles.wheelContainer} ${size === 'large' ? styles.large : ''} ${isSpinning ? styles.spinning : ''} ${isPaused ? styles.paused : ''}`}
    >
      <div className={styles.pointer} />

      <div className={styles.wheelWrapper}>
        <svg className={styles.wheelSvg} viewBox="0 0 300 300">
          <defs>
            <radialGradient id="baseBlue" cx="50%" cy="50%" r="65%">
              <stop offset="0%" stopColor="#14213d" />
              <stop offset="100%" stopColor="#080e1a" />
            </radialGradient>

            <radialGradient id="basePurple" cx="50%" cy="50%" r="65%">
              <stop offset="0%" stopColor="#2c0b42" />
              <stop offset="100%" stopColor="#12021c" />
            </radialGradient>

            <radialGradient id="glowBlue" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="#00f0ff" stopOpacity="0.28" />
              <stop offset="70%" stopColor="#0099cc" stopOpacity="0.16" />
              <stop offset="100%" stopColor="#00f0ff" stopOpacity="0.0" />
            </radialGradient>

            <radialGradient id="glowPurple" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="#ff00d4" stopOpacity="0.32" />
              <stop offset="70%" stopColor="#b30095" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#ff00d4" stopOpacity="0.0" />
            </radialGradient>
          </defs>

          <g
            style={{
              transform: `rotate(${rotation}deg)`,
              transformOrigin: '150px 150px',
              transition: 'transform 4s cubic-bezier(0.25, 0.1, 0.25, 1)',
            }}
          >
            <circle cx="150" cy="150" r="148" fill="#070a12" />

            {prizes.map((prize, i) => {
              if (isHalfWheel && !isSpinning && i < Math.floor(prizes.length / 2)) {
                return null;
              }

              const isEven = i % 2 === 0;
              const baseFill = isEven ? 'url(#baseBlue)' : 'url(#basePurple)';
              const glowFill = isEven ? 'url(#glowBlue)' : 'url(#glowPurple)';
              const strokeColor = isEven ? '#00f0ff' : '#ff00e6';

              const midAngle = (i + 0.5) * sliceAngle;
              const chamberD = createChamberPath(i);
              const isSpins = prize.type === 'spins';

              return (
                <g key={prize.id}>
                  <path d={chamberD} fill={baseFill} />

                  <path
                    d={chamberD}
                    fill={glowFill}
                    className={styles.innerChamberGlow}
                    style={{ animationDelay: `${i * 0.18}s` }}
                  />

                  <path
                    d={chamberD}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth="1.5"
                    className={styles.sparkleWave}
                    style={{
                      animationDelay: `${i * 0.18}s`,
                      color: strokeColor,
                    }}
                  />

                  {isSpins ? (
                    <g
                      transform={`translate(150, 150) rotate(${midAngle}) translate(98, 0) rotate(90)`}
                    >
                      <text x="0" y="-8" textAnchor="middle" className={styles.spinsNumber}>
                        {prize.label}
                      </text>
                      <text x="0" y="8" textAnchor="middle" className={styles.spinsText}>
                        Free
                      </text>
                      <text x="0" y="21" textAnchor="middle" className={styles.spinsTextGlow}>
                        SPINS
                      </text>
                    </g>
                  ) : (
                    <g transform={`translate(150, 150) rotate(${midAngle}) translate(95, 0)`}>
                      <text x="0" y="5" textAnchor="middle" className={styles.segmentLabel}>
                        {prize.label}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

            <circle
              cx="150"
              cy="150"
              r="148"
              fill="none"
              stroke="#ff00d4"
              strokeWidth="3.2"
              className={styles.outerRim}
            />
            <circle cx="150" cy="150" r="148" fill="none" stroke="#00f0ff" strokeWidth="1" />

            {prizes.map((_, i) => {
              const angle = (i * sliceAngle * Math.PI) / 180;
              const cx = 150 + 144 * Math.cos(angle);
              const cy = 150 + 144 * Math.sin(angle);

              return (
                <circle
                  key={`dot-${i}`}
                  cx={cx}
                  cy={cy}
                  r="3.5"
                  fill="#ffffff"
                  className={styles.ledDot}
                  style={{ animationDelay: `${i * 0.16}s` }}
                />
              );
            })}

            <circle cx="150" cy="150" r="30" fill="#02122c" stroke="#00f0ff" strokeWidth="2.5" />
            <circle cx="150" cy="150" r="13" fill="#ff00d4" />
          </g>
        </svg>
      </div>
    </div>
  );
};

export default PrizeWheel;
